// src/shared/changelog/types.ts
import type { ChangelogProduct } from "./products";

/**
 * Nature of a change, rendered as a colored `StatusBadge`. Closed set — the
 * same six values the entry files and the backend `category` column accept.
 */
export type ChangeCategory =
  | "feature"
  | "improvement"
  | "fix"
  | "security"
  | "deprecation"
  | "breaking";

/**
 * Tenant types an entry is relevant to. Mirrors `userProfile.TenantType`, so
 * the server can filter on it with the values the app already gates on.
 * Absent or `null` means every tenant type.
 */
export type ChangelogAudience = "CUSTOMER" | "PARTNER" | "GLOBAL_ADMIN";

/**
 * One stop of a guided walkthrough.
 *
 * `selector` is a CSS selector for the element to spotlight. It is optional
 * because a step with no anchor is a legitimate step, not a broken one — the
 * tour renders a centered card for it, which is also what happens when an
 * anchor is authored but not currently mounted. That fallback is why a stale
 * selector degrades instead of breaking, and why nothing here can be validated
 * against the DOM at author time.
 */
export interface ChangelogWalkthroughStep {
  selector?: string;
  title: string;
  body: string;
}

/**
 * Where a change landed (CTX-509), narrowed to a single authored key in
 * CTX-519.
 *
 * An entry otherwise records no location: `products` is the finest grain
 * available, and the backend's `component` is the repo that shipped it, so
 * neither the What's new modal nor the SideMenu can point at the screen that
 * actually changed. This is that missing coordinate.
 *
 * **The route is the whole of it.** The menu row this belongs to used to be
 * authored beside it as a `menuItemId`; it is now derived from `path` through
 * `findMenuItemByPath`, which the rail already uses to decide which row a URL
 * lights up. Asking an author to name the row as well is asking them to repeat
 * what the menu already knows, and to keep the two in step the day a route
 * moves.
 *
 * Authoring rule that keeps `selector` honest: the PR shipping a change adds
 * the `data-testid` its step points at, in that same PR. The entry is already
 * written there (docs/CHANGELOG_STRATEGY.md §6), so the anchor costs nothing
 * extra — and a selector written later, against a screen nobody re-read, is a
 * step that silently falls back to a centered card.
 */
export interface ChangelogWalkthrough {
  /** In-app route the change landed on, e.g. `/insight/topology`. */
  path: string;
  /**
   * The tour, when there is one.
   *
   * Optional because pointing at a screen and touring it are two decisions:
   * `path` answers "where did this land", `steps` answers "what should I point
   * out once we are there". Absent, the CTA still navigates and simply spotlights
   * nothing on arrival. Present, it must not be empty — an empty array is a
   * tour with nothing in it, which is an authoring mistake rather than a
   * choice.
   */
  steps?: ChangelogWalkthroughStep[];
}

/**
 * One customer-visible change — the only unit of the changelog.
 *
 * There is deliberately no authored container above this: the month headings on
 * the page are derived from `releasedAt`. The fields mirror the
 * `ChangelogEntries` columns (docs/CHANGELOG_STRATEGY.md §5.1) so moving the
 * feed to the API later is a data-source swap, not a reshape.
 */
export interface ChangelogEntry {
  /**
   * Stable id — the entry file's path without extension, e.g.
   * `kontrol-edge/2026-08-04-sdwan-overview`. Doubles as the deep-link anchor
   * (`/changelog#<entryKey>`), the React key, and the ingest idempotency key.
   */
  entryKey: string;
  /** Products this change belongs to; at least one. Drives the product filter. */
  products: ChangelogProduct[];
  /** Tenant types it is relevant to. Absent = all of them. */
  audiences?: ChangelogAudience[] | null;
  category: ChangeCategory;
  title: string;
  description: string;
  /**
   * What the customer has to do, when they have to do something — deprecations,
   * breaking changes, migrations. Rendered as a callout, and the one thing
   * someone scanning a changelog is actually looking for.
   */
  actionRequired?: string | null;
  /** The month's headline change; rendered with emphasis. */
  isHighlight?: boolean;
  /**
   * Whether this entry also goes out on the **public** feed, on top of the
   * authenticated one — gate 4 of `docs/CHANGELOG_STRATEGY.md` §4.5.
   *
   * **Required of an authored entry, with no default.** Both defaults are
   * worse than forcing the decision: defaulting to `true` means one forgotten
   * flag publishes business detail to the internet, indexed and permanent;
   * defaulting to `false` means the public feed stays empty because nobody
   * remembers to turn it on.
   *
   * Never `true` alongside `audiences` — a public reader has no tenant type,
   * so anything scoped to specific ones must not reach the open feed. The
   * entry linter enforces the same pairing on the YAML files.
   *
   * **Optional on this type since CTX-202**, because the type now also
   * describes what `GET /api/v2/changelog` returns, and the endpoint
   * deliberately does not serialize this field: it routes an entry to the
   * public feed and means nothing to an authenticated reader. The decision is
   * still mandatory where it is made — the entry files, where the linter
   * enforces it. Since CTX-615 nothing in this repo reads it: the public feed
   * is projected from the table by `chopper-backend`, which owns that rule now.
   */
  isPublic?: boolean;
  /** ISO `YYYY-MM-DD`. Drives sort and the derived month grouping. */
  releasedAt: string;
  /**
   * Where this change landed, when it is somewhere a user can be taken.
   *
   * Optional and absent for most entries: a backend change, a policy change or
   * anything without a screen has no walkthrough, and an entry authored before
   * CTX-509 has none either. Both consumers treat absence as "no CTA, no tag"
   * rather than as a gap to fill in.
   */
  walkthrough?: ChangelogWalkthrough | null;
}

export const CHANGE_CATEGORY_LABEL: Record<ChangeCategory, string> = {
  feature: "New",
  improvement: "Improved",
  fix: "Fix",
  security: "Security",
  deprecation: "Deprecated",
  breaking: "Breaking",
};
